import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useTheme } from 'styled-components'
import { useGetQuery, usePostQuery } from 'hooks'
import { Box, Button, Card, Checkbox, Dropdown, Input, Title } from 'components'
import { SearchTypeahead } from 'components/forms'

const emptyVariant = { key: '', name: '', value: '{}' }
const userLabel = (u) => u.username || u.full_name || u.common_name || u.id

export const AdminFeatureFlag = () => {
  const { id } = useParams()
  const theme = useTheme()
  const searchInputStyle = {
    height: `${theme.defaults('height')}rem`,
    width: '200px',
    boxSizing: 'border-box',
    color: theme.colors('text'),
    background: theme.colors('chrome'),
    border: `1px solid ${theme.colors('foreground', 0.15)}`,
    borderRadius: theme.defaults('radius'),
    fontSize: theme.font('md'),
    fontFamily: theme.monoFont,
    padding: `0 ${theme.defaults('padding')}`,
    outline: 'none'
  }
  const searchDropdownStyle = {
    background: theme.colors('chrome'),
    border: `1px solid ${theme.colors('foreground', 0.15)}`,
    borderRadius: theme.defaults('radius')
  }
  const searchItemStyle = {
    padding: '0.3rem 0.6rem',
    cursor: 'pointer',
    fontSize: theme.font('sm'),
    fontFamily: theme.monoFont,
    color: theme.colors('text'),
    borderBottom: `1px solid ${theme.colors('foreground', 0.1)}`
  }
  const { data, refetch } = useGetQuery({ url: `/admin/feature_flags/${id}` })
  const { post: updateFlag } = usePostQuery({ url: `/admin/feature_flags/${id}`, method: 'PUT' })
  const { post: createVariant } = usePostQuery({ url: `/admin/feature_flags/${id}/variants` })
  const { post: updateVariant } = usePostQuery({ method: 'PUT' })
  const { post: deleteVariant } = usePostQuery({ method: 'DELETE' })
  const { post: setOverride } = usePostQuery({ url: `/admin/feature_flags/${id}/overrides`, method: 'PUT' })
  const { post: removeOverride } = usePostQuery({ method: 'DELETE' })

  const [detailsDraft, setDetailsDraft] = useState(null)
  const [newVariant, setNewVariant] = useState(emptyVariant)
  const [newOverrideUser, setNewOverrideUser] = useState(null)
  const [overrideVariantId, setOverrideVariantId] = useState('')
  const [valueDrafts, setValueDrafts] = useState({})
  const [errors, setErrors] = useState({})

  const flag = data?.feature_flag
  const variants = flag?.FeatureFlagVariants || []
  const overrides = flag?.FeatureFlagOverrides || []
  const details = detailsDraft ?? { name: flag?.name || '', description: flag?.description || '' }

  if (!flag) return null

  const saveDetails = () => {
    updateFlag({ name: details.name, description: details.description }).then(() => {
      setDetailsDraft(null)
      refetch(true)
    })
  }

  const toggleEnabled = (enabled) => {
    updateFlag({ enabled }).then(() => refetch(true))
  }

  const setDefaultVariant = (defaultVariantKey) => {
    updateFlag({ default_variant_key: defaultVariantKey }).then(() => refetch(true))
  }

  const addVariant = () => {
    if (!newVariant.key || !newVariant.name) return
    let value
    try {
      value = JSON.parse(newVariant.value || '{}')
    } catch {
      setErrors((prev) => ({ ...prev, newVariant: 'Invalid JSON' }))
      return
    }
    setErrors((prev) => ({ ...prev, newVariant: null }))
    createVariant({ key: newVariant.key, name: newVariant.name, value }).then(() => {
      setNewVariant(emptyVariant)
      refetch(true)
    })
  }

  const saveVariantValue = (variant) => {
    const draft = valueDrafts[variant.id] ?? JSON.stringify(variant.value)
    let value
    try {
      value = JSON.parse(draft)
    } catch {
      setErrors((prev) => ({ ...prev, [variant.id]: 'Invalid JSON' }))
      return
    }
    setErrors((prev) => ({ ...prev, [variant.id]: null }))
    updateVariant({ value }, `/admin/feature_flags/${id}/variants/${variant.id}`).then(() => refetch(true))
  }

  const removeVariant = (variant) => {
    deleteVariant({}, `/admin/feature_flags/${id}/variants/${variant.id}`).then(() => refetch(true))
  }

  const addOverride = () => {
    if (!newOverrideUser?.id || !overrideVariantId) return
    setOverride({ user_id: newOverrideUser.id, feature_flag_variant_id: overrideVariantId }).then((res) => {
      if (res?.feature_flag_override) {
        setNewOverrideUser(null)
        setOverrideVariantId('')
        refetch(true)
      } else {
        setErrors((prev) => ({ ...prev, newOverride: res?.error || 'Could not set override' }))
      }
    })
  }

  const dropOverride = (userId) => {
    removeOverride({}, `/admin/feature_flags/${id}/overrides/${userId}`).then(() => refetch(true))
  }

  const variantOptions = variants.map((v) => [v.name, v.id])
  const defaultVariantOptions = variants.map((v) => [v.name, v.key])

  return (
    <Card>
      <Box css="margin:0.5rem 1rem;">
        <Link to="/admin/feature_flags">&larr; back to feature flags</Link>
      </Box>

      <Box css="display:flex;flex-direction:column;gap:0.6rem;margin:0.5rem 1rem;">
        <Title level="4">{flag.key}</Title>

        <Box css="display:flex;align-items:center;gap:0.4rem;">
          <Input name="flag-name" placeholder="name" value={details.name} onChange={(e) => setDetailsDraft({ ...details, name: e.target.value })} />
          <Input name="flag-description" placeholder="description" value={details.description} onChange={(e) => setDetailsDraft({ ...details, description: e.target.value })} />
          <Button onPress={saveDetails}>Save</Button>
        </Box>

        <Box css="display:flex;align-items:center;gap:0.4rem;">
          <Checkbox name="flag-enabled" checked={flag.enabled} onChange={(e) => toggleEnabled(e.target.checked)} />
          <span>Enabled</span>
        </Box>

        <Box css="display:flex;align-items:center;gap:0.4rem;">
          <span>Default variant</span>
          <Dropdown
            name="default-variant"
            data={defaultVariantOptions}
            value={flag.default_variant_key}
            onChange={(e) => setDefaultVariant(e.target.value)}
          />
        </Box>
      </Box>

      <Box css="margin:0.5rem 1rem;">
        <Title level="6">Variants</Title>
        {variants.map((v) => (
          <Box key={v.id} css="display:flex;align-items:center;gap:0.3rem;margin:0.2rem 0;">
            <span style={{ minWidth: '80px' }}>{v.key}</span>
            <Input
              name={`variant-value-${v.id}`}
              value={valueDrafts[v.id] ?? JSON.stringify(v.value)}
              onChange={(e) => setValueDrafts((prev) => ({ ...prev, [v.id]: e.target.value }))}
              error={errors[v.id] ? { [`variant-value-${v.id}`]: errors[v.id] } : {}}
            />
            <Button onPress={() => saveVariantValue(v)}>Save</Button>
            <Button onPress={() => removeVariant(v)}>Delete</Button>
          </Box>
        ))}
        <Box css="display:flex;align-items:center;gap:0.3rem;margin:0.2rem 0;">
          <Input name="new-variant-key" placeholder="key" value={newVariant.key} onChange={(e) => setNewVariant((prev) => ({ ...prev, key: e.target.value }))} />
          <Input name="new-variant-name" placeholder="name" value={newVariant.name} onChange={(e) => setNewVariant((prev) => ({ ...prev, name: e.target.value }))} />
          <Input name="new-variant-value" placeholder="{}" value={newVariant.value} onChange={(e) => setNewVariant((prev) => ({ ...prev, value: e.target.value }))} />
          <Button onPress={addVariant}>Add variant</Button>
        </Box>
        {errors.newVariant && <span>{errors.newVariant}</span>}
      </Box>

      <Box css="margin:0.5rem 1rem;">
        <Title level="6">Overrides</Title>
        {overrides.map((o) => (
          <Box key={o.id} css="display:flex;align-items:center;gap:0.3rem;margin:0.2rem 0;">
            <span>{o.User?.email || o.user_id}</span>
            <span>&rarr; {o.FeatureFlagVariant?.key}</span>
            <Button onPress={() => dropOverride(o.user_id)}>Remove</Button>
          </Box>
        ))}
        <Box css="display:flex;align-items:center;gap:0.3rem;margin:0.2rem 0;">
          <SearchTypeahead
            value={newOverrideUser?.id}
            items={newOverrideUser ? [newOverrideUser] : []}
            searchUrl="/users/search"
            extractResults={(data) => data?.users ?? []}
            minChars={2}
            getLabel={userLabel}
            onSelect={(id, user) => setNewOverrideUser(user)}
            placeholder="Search user…"
            inputStyle={searchInputStyle}
            dropdownStyle={searchDropdownStyle}
            itemStyle={searchItemStyle}
          />
          <Dropdown
            name="new-override-variant"
            data={variantOptions}
            value={overrideVariantId}
            onChange={(e) => setOverrideVariantId(e.target.value)}
          />
          <Button onPress={addOverride} disabled={!variants.length}>Pin user</Button>
        </Box>
        {errors.newOverride && <span>{errors.newOverride}</span>}
      </Box>
    </Card>
  )
}
